<script setup>
import { computed, ref } from 'vue'
import { DxDataGrid, DxColumn } from 'devextreme-vue/data-grid'
import { getI18n } from '@/plugins/i18n'
import { useDetectDevice } from '@/composables/device'
import { remToPx } from '@/lib/utils'
import { EXPORT_FORMAT, bindHeaderTouchScroll, useDefaultExport } from '@/composables/data-grid'
import { ROI_COMPARISON_LIMITS, useRoiComparisonFormat } from '@/composables/roi-comparison'

import RoiComparisonValue from '@/components/roi/RoiComparisonValue.vue'

const ITEM_COLUMN_MIN_WIDTH_REM = Object.freeze({ MOBILE: 6.5, DESKTOP: 11 })
const GRID_SORTING = Object.freeze({ mode: 'none' })
const GRID_PAGING = Object.freeze({ enabled: false })
const GRID_GROUPING = Object.freeze({ autoExpandAll: true, allowCollapsing: false })
const GRID_GROUP_PANEL = Object.freeze({ visible: false })

const props = defineProps({
  comparison: {
    type: Object,
    required: true,
  },
})

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { definitions, metricLabel, groupLabel, typeLabel, formatMetric, visibleMetrics, bestIds, itemName, itemAvatar, referenceRow } = useRoiComparisonFormat()

const gridRef = ref(null)
const itemColumnMinWidth = computed(() => remToPx(isMobile.value ? ITEM_COLUMN_MIN_WIDTH_REM.MOBILE : ITEM_COLUMN_MIN_WIDTH_REM.DESKTOP))

const groups = computed(() => definitions[props.comparison.type].groups)

const metricRows = computed(() => groups.value.flatMap((group, groupOrder) => visibleMetrics(group.metrics, props.comparison.rows)
  .map(metric => ({ key: metric.key, groupOrder, label: metricLabel(metric), metric, best: bestIds(metric, props.comparison.rows) }))))

const groupText = (groupOrder) => (groups.value[groupOrder] ? groupLabel(groups.value[groupOrder]) : '')
const columnField = (id) => `item_${id}`
const rowsByField = computed(() => new Map(props.comparison.rows.map(row => [columnField(row.id), row])))
const rowOf = (column) => rowsByField.value.get(column.dataField)

const orderedRows = computed(() => {
  const reference = referenceRow(props.comparison)
  if (!isMobile.value || !reference) return props.comparison.rows
  return [reference, ...props.comparison.rows.filter(row => row !== reference)]
})

const itemColumns = computed(() => orderedRows.value.map(row => ({
  dataField: columnField(row.id),
  caption: itemName(props.comparison.type, row.item),
  calculateCellValue: (metricRow) => formatMetric(metricRow.metric, row.values),
})))

const onCellPrepared = (e) => {
  if (e.column.dataField === columnField(props.comparison.referenceId)) e.cellElement.classList.add('roi-grid-reference')
}

const onContentReady = (e) => {
  if (isMobile.value) bindHeaderTouchScroll(e.component)
}

const customizeGroupCell = (cell, gridCell) => {
  if (gridCell?.rowType === 'group' && gridCell.column?.dataField === 'groupOrder') cell.value = groupText(gridCell.value)
}

const exportAsync = async(format) => {
  const { exportToExcel, exportToPdf } = useDefaultExport({
    fileName: `${i18n.t('menu.roi_comparison')} - ${typeLabel(props.comparison.type)}`,
    customizeCellExcel: ({ excelCell, gridCell }) => customizeGroupCell(excelCell, gridCell),
    customizeCellPdf: ({ pdfCell, gridCell }) => customizeGroupCell(pdfCell, gridCell),
  })
  const e = { component: gridRef.value.instance, format }
  await (format === EXPORT_FORMAT.PDF ? exportToPdf(e) : exportToExcel(e))
}

defineExpose({ exportAsync })
</script>

<template>
  <dx-data-grid
    ref="gridRef"
    :data-source="metricRows"
    key-expr="key"
    :show-borders="true"
    :show-column-lines="true"
    :hover-state-enabled="true"
    :column-auto-width="false"
    :word-wrap-enabled="isMobile"
    width="100%"
    :height="isMobile ? '100%' : undefined"
    :sorting="GRID_SORTING"
    :paging="GRID_PAGING"
    :grouping="GRID_GROUPING"
    :group-panel="GRID_GROUP_PANEL"
    :no-data-text="i18n.t('roiCompare.selectAtLeast', { min: ROI_COMPARISON_LIMITS.MIN })"
    @cell-prepared="onCellPrepared"
    @content-ready="onContentReady"
  >
    <dx-column
      data-field="groupOrder"
      :group-index="0"
      sort-order="asc"
      group-cell-template="groupCell"
    />
    <dx-column
      data-field="label"
      :caption="i18n.t('roiCompare.metric')"
      :fixed="true"
      :width="isMobile ? '7rem' : '12rem'"
    />
    <dx-column
      v-for="column in itemColumns"
      :key="column.dataField"
      :data-field="column.dataField"
      :caption="column.caption"
      :calculate-cell-value="column.calculateCellValue"
      :min-width="itemColumnMinWidth"
      header-cell-template="itemHeader"
      cell-template="valueCell"
    />
    <template #groupCell="{ data }">
      <span class="text-primary text-weight-medium">{{ groupText(data.value) }}</span>
    </template>
    <template #itemHeader="{ data }">
      <div v-if="rowOf(data.column)" class="roi-grid-header">
        <q-avatar rounded>
          <q-img v-if="itemAvatar(rowOf(data.column).item)" :src="itemAvatar(rowOf(data.column).item)" />
          <q-icon
            v-else
            :name="definitions[comparison.type].icon"
            size="sm"
            color="grey-5"
          />
        </q-avatar>
        <div class="ellipsis-2-lines q-mt-xs">{{ data.column.caption }}</div>
        <div v-if="rowOf(data.column).id === comparison.referenceId" class="row items-center no-wrap text-caption text-primary">
          <q-icon name="mdi-target" size="xs" class="q-mr-xs" />
          {{ i18n.t('common.reference') }}
        </div>
      </div>
    </template>
    <template #valueCell="{ data }">
      <roi-comparison-value
        v-if="rowOf(data.column)"
        :comparison="comparison"
        :metric="data.data.metric"
        :row="rowOf(data.column)"
        :best="data.data.best"
        :text="data.value"
        :compact="isMobile"
      />
    </template>
  </dx-data-grid>
</template>

<style scoped>
.roi-grid-header {
  width: 100%;
  white-space: normal;
  text-align: left;
}
:deep(.dx-datagrid-text-content:has(.roi-grid-header)) {
  display: block;
  width: 100%;
}
:deep(.dx-row > td.roi-grid-reference) {
  background-color: color-mix(in srgb, var(--q-primary) 6%, transparent);
}
</style>
